import { CheckUserSupplierMembershipQuery } from '@/types';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { useAuth } from '@/contexts/auth-context';
import { useGeneratedCheckUserSupplierMembershipQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-user-supplier-membership.generated';

/**
 * Helper to determine if the user is a sales rep based on query data.
 * A user is considered a "sales rep" if they are an employee of the supplier business.
 */
const getIsSalesRepFromData = (
  data: CheckUserSupplierMembershipQuery | undefined,
): boolean => {
  return (data?.businesses_by_pk?.businesses_employees?.length ?? 0) > 0;
};

/**
 * Hook that checks if the current user is a sales rep for the given supplier.
 * If the user is NOT a sales rep (i.e., they are an operator/restaurant user),
 * they are automatically redirected to the Operator Portal.
 *
 * @param supplierUUID - The UUID of the supplier to check membership for
 * @returns Object containing:
 *   - `isSalesRep` - Whether the user is a sales rep for this supplier
 *   - `loading` - Whether the check is still in progress
 */
// TODO [DSR-3622] refactor this for general use when Pricing Management is implemented
export const useRedirectOperatorUsers = (supplierUUID: string | undefined) => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const { switchToExperience } = useExperienceStore();
  const shouldSkip = !authenticatedUserUUID || !supplierUUID;

  const onCompleted = (completedData: CheckUserSupplierMembershipQuery) => {
    const isSalesRep = getIsSalesRepFromData(completedData);
    if (!isSalesRep) {
      switchToExperience(ExperienceMode.OPERATOR_PORTAL);
    }
  };

  const variables =
    authenticatedUserUUID && supplierUUID
      ? {
          supplierUUID,
          userUUID: authenticatedUserUUID,
        }
      : undefined;

  const { data, previousData, loading } =
    useGeneratedCheckUserSupplierMembershipQuery({
      onCompleted,
      skip: shouldSkip,
      variables,
    });

  // When `supplierUUID` changes, `data` is set to `undefined` which temporarily fails the
  // `getIsSalesRepFromData` check. So while we're loading new membership data,
  // use the previous set to maintain validation.
  const verdictData = loading ? (data ?? previousData) : data;

  return {
    // A user is considered a "sales rep" if they are an employee of the supplier business.
    // This is determined by checking if the user has an entry in businesses_employees for
    // the supplier. Restaurant employees (customers) do not have this relationship and
    // should not have access to the Sales Portal.
    isSalesRep: getIsSalesRepFromData(verdictData),
    // Return true for loading if query is skipped (waiting for auth)
    // or if the query is actually in flight
    loading: shouldSkip || loading,
  };
};
